import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { ADDON_HUB_PRESELECT_DEFAULTS } from "app/contexts/NoTouchPurchaseContext/useNoTouchPurchaseProvider";
import { translate } from "app/filters/i18nFilter";
import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import type {
    PremiumFeatureActions,
    PremiumFeatureActionsParams,
} from "app/pages/sales-intelligence/pages/premium-features/types";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { openHooksModalV5 } from "app/services/ModalService";
import { goToAddonsPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToAddonsPage";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import {
    hasAddonsEnabled,
    isNoTouch,
    isAfterTrial,
    isTraditionalTrialUser,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { requestFeature } from "pro-features/components/Modals/src/HookModal/services/requestFeature";

import { PremiumFeatures, PremiumFeaturesUserRole } from "../types/enums";

const openNtAddonHub = (packageIds?: string[]) => {
    if (!hasAddonsEnabled(swSettings)) {
        return;
    }
    goToAddonsPage({ packageIds });
};

let premiumFeatureActionsCache: Record<PremiumFeatures, Record<PremiumFeaturesUserRole, PremiumFeatureActions>> | null =
    null;

const getPremiumFeatureActionsMap = (): Record<
    PremiumFeatures,
    Record<PremiumFeaturesUserRole, PremiumFeatureActions>
> => {
    if (!premiumFeatureActionsCache) {
        premiumFeatureActionsCache = {
            [PremiumFeatures.PrepareMeetings]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentMeetingRoom(),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentMeetingRoom(),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("PrepareMeetings");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentMeetingRoom(),
                },
            },
            [PremiumFeatures.ExtensionIntegration]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.download-extension"),
                    onClick: () => window.open(EXTENSION_LINK),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.download-extension"),
                    onClick: () => window.open(EXTENSION_LINK),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.download-extension"),
                    onClick: () => window.open(EXTENSION_LINK),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.download-extension"),
                    onClick: () => window.open(EXTENSION_LINK),
                },
            },
            [PremiumFeatures.AiProspecting]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAdvancedSearchPage(),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAdvancedSearchPage(),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("AiProspecting");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAdvancedSearchPage(),
                },
            },
            [PremiumFeatures.Integrations]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Integrations");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Integrations");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Integrations");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Integrations");
                    },
                    useCustomFunction: true,
                },
            },

            [PremiumFeatures.Enrichment]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Enrichment");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Enrichment");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Enrichment");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("Enrichment");
                    },
                    useCustomFunction: true,
                },
            },

            [PremiumFeatures.AiOutreach]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentCraftEmail(),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentCraftEmail(),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("AiOutreach");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.try_now"),
                    onClick: () => siNavigator.goToAIAgentCraftEmail(),
                },
            },

            [PremiumFeatures.ContactCredits]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SalesSolutionRevealDefaultSIRedirect"),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SalesSolutionRevealDefaultSIRedirect"),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SalesSolutionRevealDefaultSIRedirect"),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SalesSolutionRevealDefaultSIRedirect"),
                },
            },
            [PremiumFeatures.CountryFilters]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("WebCountrySIRedirect"),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("WebCountrySIRedirect"),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("WebCountrySIRedirect"),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("WebCountrySIRedirect"),
                },
            },

            [PremiumFeatures.DataCredits]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.upgrade"),
                    onClick: () =>
                        openNtAddonHub([ADDON_HUB_PRESELECT_DEFAULTS[ADDON_TYPE_NAME_DICT.SiDataCredits1000]]),
                    useCustomFunction: true,
                    buttonType: "trialOutline",
                    iconName: "sparkles",
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("DataCredits");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("DataCredits");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("DataCredits");
                    },
                    useCustomFunction: true,
                },
            },

            [PremiumFeatures.AppsSdkData]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("AppLeadGenerator"),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5(HooksModalV5Key.AppLeadGenerator),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5(HooksModalV5Key.AppLeadGenerator),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5(HooksModalV5Key.AppLeadGenerator),
                },
            },

            [PremiumFeatures.AdditionalUsers]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.upgrade"),
                    onClick: () =>
                        openNtAddonHub([ADDON_HUB_PRESELECT_DEFAULTS[ADDON_TYPE_NAME_DICT.AdditionalUsersDynamic]]),
                    useCustomFunction: true,
                    buttonType: "trialOutline",
                    iconName: "sparkles",
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.upgrade"),
                    onClick: () => goToPackagesPage(),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.upgrade"),
                    onClick: () => goToPackagesPage(),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("AdditionalUsers");
                    },
                    useCustomFunction: true,
                },
            },

            [PremiumFeatures.LeadsEnrichmentApi]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("LeadsEnrichmentApi");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("LeadsEnrichmentApi");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("LeadsEnrichmentApi");
                    },
                    useCustomFunction: true,
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.contact_us"),
                    onClick: () => {
                        requestFeature("LeadsEnrichmentApi");
                    },
                    useCustomFunction: true,
                },
            },

            [PremiumFeatures.ContactApi]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("ContactsApi"),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("ContactsApi"),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("ContactsApi"),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("ContactsApi"),
                },
            },

            [PremiumFeatures.SignalsApi]: {
                [PremiumFeaturesUserRole.NoTouch]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SignalsApi"),
                },
                [PremiumFeaturesUserRole.Trial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SignalsApi"),
                },
                [PremiumFeaturesUserRole.AfterTrial]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SignalsApi"),
                },
                [PremiumFeaturesUserRole.PayingUser]: {
                    buttonText: translate("si.common.learn_more"),
                    onClick: () => openHooksModalV5("SignalsApi"),
                },
            },
        };
    }

    return premiumFeatureActionsCache;
};

export const getPremiumFeaturesActions = ({
    type,
    onCustomClick,
    role,
}: PremiumFeatureActionsParams): PremiumFeatureActions => {
    const premiumFeature = getPremiumFeatureActionsMap()[type][role];
    if (premiumFeature) {
        if (onCustomClick && premiumFeature.useCustomFunction) {
            return {
                ...premiumFeature,
                onClick: () => {
                    const actions = getPremiumFeaturesActions({ type, role });
                    actions.onClick();
                    onCustomClick?.();
                },
            };
        }

        return premiumFeature;
    }
    return {
        buttonText: translate("si.common.contact_us"),
        onClick: () => {
            Sentry.error(
                `Error untracked premium feature action - type: ${type}, role: ${role}`,
                new Error("Untracked premium feature action"),
            );
        },
    };
};

export const getUserRole = (): PremiumFeaturesUserRole => {
    if (isNoTouch(swSettings)) {
        return PremiumFeaturesUserRole.NoTouch;
    }

    if (isTraditionalTrialUser(swSettings)) {
        return PremiumFeaturesUserRole.Trial;
    }

    if (isAfterTrial(swSettings)) {
        return PremiumFeaturesUserRole.AfterTrial;
    }

    return PremiumFeaturesUserRole.PayingUser;
};
